import React from "react";
import { FiWind, FiThermometer, FiDroplet, FiAlertTriangle, FiActivity } from "react-icons/fi";
import { AqiGauge, AqiBadge, KpiCard, aqiMeta, pm25Color, WindArrow } from "./aqmHelpers";

const AqmOutdoorOverviewPanel = ({ d }) => {
  const { summary, stations, weather, weeklyAqi } = d;
  const m = aqiMeta(summary.overallAqi);
  const maxWeekly = Math.max(...weeklyAqi.map(w => w.aqi));

  return (
    <div className="aqm-overview">

      {/* ── Hero ── */}
      <div className="aqm-ov-hero">
        <div className="aqm-aqi-hero-card">
          <AqiGauge aqi={summary.overallAqi} size={120} />
          <div>
            <div className="aqm-aqi-hero-label">City-Wide AQI</div>
            <div className="aqm-aqi-hero-val" style={{ color: m.color }}>{summary.overallAqi}</div>
            <AqiBadge aqi={summary.overallAqi} />
            <div className="aqm-aqi-hero-site">{d.site.name}</div>
            <div className="aqm-aqi-hero-loc">{d.site.location}</div>
            <div className="aqm-aqi-hero-meta">{d.site.onlineStations}/{d.site.totalStations} Stations Online · {summary.activeAlerts} Active Alerts</div>
          </div>
        </div>

        {/* Weather strip */}
        <div className="aqm-ov-weather-card">
          <div className="aqm-section-title">Current Conditions</div>
          <div className="aqm-weather-row">
            <WindArrow deg={weather.windDeg} size={56} />
            <div className="aqm-weather-params">
              <div className="aqm-wp-item"><span className="aqm-wp-val">🌡 {weather.temp}°C</span><span className="aqm-wp-lbl">Temperature</span></div>
              <div className="aqm-wp-item"><span className="aqm-wp-val">💧 {weather.humidity}%</span><span className="aqm-wp-lbl">Humidity</span></div>
              <div className="aqm-wp-item"><span className="aqm-wp-val">💨 {weather.windSpeed} km/h</span><span className="aqm-wp-lbl">{weather.windDir} Wind</span></div>
              <div className="aqm-wp-item"><span className="aqm-wp-val">☀ UV {weather.uvIndex}</span><span className="aqm-wp-lbl">UV Index</span></div>
            </div>
          </div>
          <div className="aqm-section-title" style={{ marginTop: 12 }}>5-Day Forecast</div>
          <div className="aqm-forecast-row">
            {weather.forecast.map((f, i) => {
              const fm = aqiMeta(f.aqi);
              return (
                <div key={i} className="aqm-forecast-chip">
                  <div style={{ fontSize: 11, color: "#6b7280" }}>{f.day}</div>
                  <div style={{ color: fm.color, fontWeight: 700 }}>{f.aqi}</div>
                  <div style={{ fontSize: 9, color: "#9ca3af" }}>{f.condition}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Station summary */}
        <div className="aqm-ov-pollutant-card">
          <div className="aqm-section-title">Station AQI Overview</div>
          {stations.slice(0, 5).map(s => {
            const sm = aqiMeta(s.aqi);
            return (
              <div key={s.id} className="aqm-floor-row">
                <div className="aqm-floor-id" style={{ background: sm.color + "22", color: sm.color, fontSize: 9 }}>{s.shortName}</div>
                <div className="aqm-floor-name" style={{ fontSize: 11 }}>{s.name.split("—")[0].trim()}</div>
                <div style={{ flex: 1, padding: "0 8px" }}>
                  <div style={{ background: "#f3f4f6", borderRadius: 4, height: 6, overflow: "hidden" }}>
                    <div style={{ width: `${Math.min(100, (s.aqi / 300) * 100)}%`, background: sm.color, height: "100%", borderRadius: 4 }} />
                  </div>
                </div>
                <span style={{ color: sm.color, fontWeight: 700, fontSize: 13, minWidth: 32 }}>{s.aqi}</span>
                <span className="aqm-badge" style={{ background: sm.bg, color: sm.text, fontSize: 9 }}>{sm.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="aqm-kpi-grid">
        <KpiCard label="AQI"           value={summary.overallAqi}     color={m.color}           icon={FiActivity}     sub={m.label} />
        <KpiCard label="PM2.5"         value={summary.pm25} unit="µg/m³" color={pm25Color(summary.pm25)} icon={FiWind} sub="NAAQS Limit: 60 µg/m³" alert={summary.pm25 > 60 ? "EXCEEDS limit!" : null} />
        <KpiCard label="PM10"          value={summary.pm10} unit="µg/m³" color={pm25Color(summary.pm10 / 2)} icon={FiWind} sub="NAAQS Limit: 100 µg/m³" alert={summary.pm10 > 100 ? "EXCEEDS limit!" : null} />
        <KpiCard label="NO₂"          value={summary.no2}  unit="µg/m³" color="#f97316"         icon={FiWind}         sub="NAAQS Limit: 80 µg/m³" />
        <KpiCard label="SO₂"          value={summary.so2}  unit="µg/m³" color="#8b5cf6"         icon={FiWind}         sub="NAAQS Limit: 80 µg/m³" />
        <KpiCard label="CO"           value={summary.co}   unit="mg/m³" color="#f59e0b"         icon={FiWind}         sub="NAAQS Limit: 2 mg/m³ (8h)" />
        <KpiCard label="Temperature"  value={summary.temperature} unit="°C" color="#f97316"    icon={FiThermometer}  sub="Current ambient" />
        <KpiCard label="Active Alerts" value={summary.activeAlerts}          color="#ef4444"   icon={FiAlertTriangle} alert="Check alerts tab" />
      </div>

      {/* ── Bottom ── */}
      <div className="aqm-ov-bottom">
        <div className="aqm-ov-floor-card">
          <div className="aqm-section-title">All Station Status</div>
          {stations.map(s => {
            const sm = aqiMeta(s.aqi);
            return (
              <div key={s.id} className="aqm-floor-row">
                <div className="aqm-floor-id" style={{ background: s.status === "Online" ? "#d1fae5" : "#fee2e2", color: s.status === "Online" ? "#065f46" : "#7f1d1d", fontSize: 9 }}>
                  {s.status === "Online" ? "●" : "○"}
                </div>
                <div className="aqm-floor-name" style={{ fontSize: 12 }}>{s.name}</div>
                <div style={{ flex: 1 }} />
                <span style={{ color: sm.color, fontWeight: 700, fontSize: 14 }}>{s.aqi}</span>
                <span className="aqm-badge" style={{ background: sm.bg, color: sm.text, fontSize: 10 }}>{sm.label}</span>
              </div>
            );
          })}
        </div>

        <div className="aqm-ov-weekly-card">
          <div className="aqm-section-title">Weekly AQI Trend (City Average)</div>
          <div className="aqm-bar-chart" style={{ height: 160 }}>
            {weeklyAqi.map(day => {
              const dm = aqiMeta(day.aqi);
              return (
                <div key={day.day} className="aqm-bar-col">
                  <div className="aqm-bar-val" style={{ color: dm.color }}>{day.aqi}</div>
                  <div className="aqm-bar-wrap">
                    <div className="aqm-bar-fill" style={{ height: `${(day.aqi / maxWeekly) * 100}%`, background: dm.color }} />
                  </div>
                  <div className="aqm-bar-lbl">{day.day}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AqmOutdoorOverviewPanel;
